import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config/frontend.config';
import AxiosInstance from '../AxiosInstance';

// Global flag to prevent duplicate permission calls
let globalPermissionFetching = false;

const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const fetchUserPermissions = async () => {
    // Global check to prevent duplicate calls
    if (globalPermissionFetching) {
      return;
    }

    try {
      globalPermissionFetching = true;
      setLoading(true);
      setError(null);

      const userId = config.getUserId();
      
      if (!userId) {
        setPermissions({});
        setUserRole(null);
        setLoading(false);
        return;
      }

      // Get user's current role from localStorage first
      const currentRole = config.getUserRole();
      setUserRole(currentRole);

      // Fetch permissions from backend with cache busting
      const timestamp = new Date().getTime();
      const response = await AxiosInstance.get(
        `${config.api.baseURL}/${config.api.apiPath}/user/permissions/${userId}?_t=${timestamp}`
      );

      if (response.data && response.data.statusCode === 200) {
        const permissions = response.data.data.permissions || {};
        const roleName = response.data.data.roleName || currentRole;
        setPermissions(permissions);
        setUserRole(roleName);
        
        // Update localStorage with role name
        if (roleName) {
          localStorage.setItem('userRole', roleName);
        }
      } else {
        // Fallback to frontend config permissions
        const fallbackPermissions = config.getRolePermissions(currentRole);
        setPermissions(fallbackPermissions);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to load permissions');
      
      // Fallback to frontend config
      const currentRole = config.getUserRole();
      const fallbackPermissions = config.getRolePermissions(currentRole);
      setPermissions(fallbackPermissions);
      setUserRole(currentRole);
    } finally {
      setLoading(false);
      globalPermissionFetching = false; // Reset global flag
    }
  };

  // Initial fetch - only run once
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      fetchUserPermissions();
    }
  }, []); // Empty dependency array - only run once on mount

  const hasPermission = (permission) => {
    // SuperAdmin has all permissions automatically
    if (userRole && userRole.toLowerCase() === 'superadmin') {
      return true;
    }
    
    if (!permissions || Object.keys(permissions).length === 0) {
      return false;
    }
    return permissions[permission] === true;
  };

  const hasAnyPermission = (permissionList) => {
    // SuperAdmin has all permissions automatically
    if (userRole && userRole.toLowerCase() === 'superadmin') {
      return true;
    }
    
    if (!permissions || Object.keys(permissions).length === 0) {
      return false;
    }
    return permissionList.some(permission => permissions[permission] === true);
  };

  const isRole = (roleName) => {
    if (!userRole) return false;
    return userRole.toLowerCase() === roleName.toLowerCase();
  };

  const isAnyRole = (roleList) => {
    if (!userRole) return false;
    return roleList.some(role => userRole.toLowerCase() === role.toLowerCase());
  };

  const refreshPermissions = useCallback(() => {
    fetchUserPermissions();
  }, []); // Empty deps - fetchUserPermissions is stable

  const value = {
    permissions,
    userRole,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    isRole,
    isAnyRole,
    refreshPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}; 